import { ParsedSkin } from "./spineParser";
import { resolveParsedSkin } from "../store/skinResolve";
import type { RelatedTemplate } from "../store/configStore";

interface RandomizeContext {
  parsedSkins: ParsedSkin[];
  templateConfig: any;
  relatedTemplates?: RelatedTemplate[];
}

interface RandomizeResult {
  selectedSkins: string[];
  selectedToningColors: Record<string, string>;
  previewToningId: string | null;
}

/**
 * 随机选择皮肤和染色方案
 * 支持主模版和关联模版的组件
 */
export function randomizeSkins(context: RandomizeContext): RandomizeResult {
  const { parsedSkins, templateConfig, relatedTemplates } = context;

  if (!templateConfig) {
    return {
      selectedSkins: [],
      selectedToningColors: {},
      previewToningId: null,
    };
  }

  const requireCategories = new Set(templateConfig.requireCategories || []);

  // 1. 收集所有已绑定到 selection 的 skinNames (主模版 + 关联模版)
  const boundSkinNames = new Set<string>();

  // 1.1 主模版
  if (templateConfig.selection) {
    const skus = templateConfig.skus || {};
    for (const selection of templateConfig.selection) {
      const skuIds = selection.skuIds || [];
      for (const skuId of skuIds) {
        const sku = skus[skuId];
        if (sku?.skinName) {
          boundSkinNames.add(sku.skinName);
        }
      }
    }
  }

  // 1.2 关联模版
  if (relatedTemplates) {
    for (const rel of relatedTemplates) {
      if (rel.config && rel.config.selection) {
        const relSkus = rel.config.skus || {};
        for (const selection of rel.config.selection) {
          const skuIds = selection.skuIds || [];
          for (const skuId of skuIds) {
            const sku = relSkus[skuId];
            if (sku?.skinName) {
              boundSkinNames.add(sku.skinName);
            }
          }
        }
      }
    }
  }

  if (boundSkinNames.size === 0) {
    return {
      selectedSkins: [],
      selectedToningColors: {},
      previewToningId: null,
    };
  }

  // 2. 按 category 分组 (主模版 + 关联模版)
  const categoryToSkins = new Map<string, string[]>();

  // Helper to add skin
  const addSkinToMap = (skin: ParsedSkin) => {
    // 只处理已绑定到 selection 的 skin
    if (!boundSkinNames.has(skin.name)) return;

    if (!categoryToSkins.has(skin.category)) {
      categoryToSkins.set(skin.category, []);
    }
    categoryToSkins.get(skin.category)!.push(skin.name);
  };

  // 2.1 主模版 skins
  for (const skin of parsedSkins) {
    addSkinToMap(skin);
  }

  // 2.2 关联模版 skins
  if (relatedTemplates) {
    for (const rel of relatedTemplates) {
      for (const skin of rel.parsedSkins) {
        addSkinToMap(skin);
      }
    }
  }

  // 2.3 仅存在于 SKU 的占位名（如静态层 skinName: static/xxx）
  for (const name of boundSkinNames) {
    let listed = false;
    for (const skins of categoryToSkins.values()) {
      if (skins.includes(name)) {
        listed = true;
        break;
      }
    }
    if (listed) continue;
    const resolved = resolveParsedSkin(
      name,
      templateConfig,
      parsedSkins,
      relatedTemplates,
    );
    if (resolved) {
      addSkinToMap(resolved);
    }
  }

  // 3. 随机选择皮肤
  const newSkins: string[] = [];
  for (const [category, skins] of categoryToSkins) {
    if (skins.length === 0) continue;

    const isRequired = requireCategories.has(category);
    // 必选 category → 必须选一个
    // 非必选 category → 50% 概率选
    if (isRequired || Math.random() < 0.5) {
      const randomSkin = skins[Math.floor(Math.random() * skins.length)];
      newSkins.push(randomSkin);
    }
  }

  // 4. 随机选择染色方案和配色 (仅主模版)
  const newToningColors: Record<string, string> = {};
  const tonings = templateConfig.tonings || [];
  for (const toning of tonings) {
    if (toning.colors && toning.colors.length > 0) {
      // 50% 概率选中该调色方案
      if (Math.random() < 0.5) {
        const randomColor =
          toning.colors[Math.floor(Math.random() * toning.colors.length)];
        newToningColors[toning.id] = randomColor.id;
      }
    }
  }

  return {
    selectedSkins: newSkins,
    selectedToningColors: newToningColors,
    previewToningId: tonings.length > 0 ? tonings[0].id : null,
  };
}
